import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import {
	getPermissions,
	IntegrationFlowKind,
	IntegrationFlowPermissionsSaveModel,
	IntegrationFlowPermissionsViewModel,
	setPermissions
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	Checkbox,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Toolbar,
	ValidationError
} from "client/Components";
import { EnvironmentFeature, getRequiredUserLicenseForEnvironmentFeature } from "client/Features/FeaturePackages/misc";
import { attachToUserAndUserGroupCollection } from "client/Features/Users/UserAndUserGroupCollection/attachable";
import { UserAndUserGroupCollection, UserAndUserGroupKind } from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useOpenDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";

type ManageIntegrationPermissionsDialogProps = {
	item: {
		id: number;
		kind: IntegrationFlowKind;
		name: string;
	};
	onClose: () => void;
};

const ManageIntegrationPermissionsDialog = (props: ManageIntegrationPermissionsDialogProps): ReactElement => {
	const {
		item,
		onClose: onCloseFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);

	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>({
		users: [],
		userGroups: []
	});

	const [formData, setFormData] = useState<IntegrationFlowPermissionsSaveModel>({
		everyoneHasAccess: true,
		whoHasAccess: {
			userIds: [],
			userGroupIds: []
		}
	});

	const formHandler: IFormHandler<IntegrationFlowPermissionsSaveModel> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const requiredUserLicense: UserLicense = getRequiredUserLicenseForEnvironmentFeature(
		EnvironmentFeature.IntegrationFramework
	);

	useEffect(
		(): (() => void) => {
			const load = async (id: number, signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const viewModel: IntegrationFlowPermissionsViewModel = await getPermissions(id, signal);

					setUsersAndUserGroupsData(viewModel.usersAndUserGroupsData);

					setFormData({
						everyoneHasAccess: viewModel.everyoneHasAccess,
						whoHasAccess: viewModel.whoHasAccess
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(item.id, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			item.id,
			onClose
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(false);

			const { validation, value } = formHandler;
			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: IntegrationFlowPermissionsSaveModel = prepareForSaving(value);

				await setPermissions(
					item.id,
					model,
					globalCancellation
				);

				alertManager.showSuccess(localize("_ChangesSaved"));

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isBusy,
			item.id,
			onClose
		]
	);

	return (
		<Dialog
			trackingId="manage-integration-permissions-dialog"
			variant="left-slider"
			onClose={onClose}
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				onClose={onClose}
				hasBorder
				title={(
					<FormattedText
						text={localize("_ManagePermissionsForName")}
						thingsToReplace={[
							{ what: "{{Name}}", filler: item.name }
						]}
					/>
				)}
			/>

			<Dialog.Body>
				<Form onSubmit="NONE" height="fill">
					<FormItem>
						<Checkbox
							{...formHandler.for("everyoneHasAccess")
								.attachToCheckbox()
							}
							text={localize("_EveryoneHasAccess")}
						/>

						<ValidationError {...formHandler.for("everyoneHasAccess").attachToValidationError()} />
					</FormItem>

					{!formHandler.value.everyoneHasAccess && (
						<FormItem height="stretch" hasNoMargin>
							<UserAndUserGroupCollection
								activeTab={ActiveTab.UserGroup}
								{...attachToUserAndUserGroupCollection(
									formHandler.for("whoHasAccess"),
									{
										kind: UserAndUserGroupKind.Edit,
										dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
									}
								)}
								allUsersAndUserGroupsData={usersAndUserGroupsData}
								requiredUserLicense={requiredUserLicense}
							/>

							<ValidationError {...formHandler.for("whoHasAccess").attachToValidationError()} />
						</FormItem>
					)}
				</Form>
			</Dialog.Body>

			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						trackingId="save"
						onClick={saveHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						trackingId="cancel"
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ManageIntegrationPermissionsDialog.displayName = "ManageIntegrationPermissionsDialog";

function prepareForSaving(model: IntegrationFlowPermissionsSaveModel): IntegrationFlowPermissionsSaveModel {
	if (model.everyoneHasAccess) {
		return {
			...model,
			whoHasAccess: { userIds: [], userGroupIds: [] }
		};
	}

	return model;
}

export type ManageIntegrationPermissionsInitialData = Omit<
	ManageIntegrationPermissionsDialogProps,
	"onClose"
>;

export function useManageIntegrationPermissionsDialog(): [
	element: ReactElement | null,
	open: (props: ManageIntegrationPermissionsInitialData) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<ManageIntegrationPermissionsDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
